import React, { useEffect, useState } from "react";
import "./App.css";
import Routes from "./router/index";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  reset_home_scroll_position,
  reset_sp_home_scroll_position,
} from "./reducer/homePageSlice";
import UsePageTracking from "./hooks/usePageTracking";
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { NetworkDiagnostics } from './utils/NetworkDiagnostics';
import { PushNotificationService } from './services/PushNotificationService';
import { LOCALSTORE, DevURL } from './constant/default';
import { registerPlugin } from '@capacitor/core';

// Define the API interface for our plugin
interface ApiConfigPlugin {
  setApiBaseUrls(options: { baseApiUrl?: string; commApiUrl?: string }): Promise<{ success: boolean }>;
  getApiBaseUrls(): Promise<{ baseApiUrl: string; commApiUrl: string }>;
}

// Function to configure native API URLs for the Android app
const configureNativeApiUrls = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Get environmental variables
      const baseUrl = process.env.REACT_APP_BASE_URL || DevURL;
      const commUrl = process.env.REACT_APP_COMM_URL || DevURL + 'commapi';

      // Enhanced logging for API configuration debugging
      console.log('🔗 CONFIGURING NATIVE API URLS - START');
      console.log('🔗 Base URL from env:', baseUrl);
      console.log('🔗 Comm URL from env:', commUrl);
      
      // Verify that the URLs are pointing to production
      if (baseUrl.includes('prod')) {
        console.log('✅ Base URL appears to be production');
      } else {
        console.warn('⚠️ Base URL does not contain "prod" - may not be production URL');
      }
      
      if (commUrl.includes('prod')) {
        console.log('✅ Comm URL appears to be production');
      } else {
        console.warn('⚠️ Comm URL does not contain "prod" - may not be production URL');
      }

      // Register and use the ApiConfig plugin with the correct type
      const ApiConfig = registerPlugin<ApiConfigPlugin>('ApiConfig');
      
      const result = await ApiConfig.setApiBaseUrls({
        baseApiUrl: baseUrl,
        commApiUrl: commUrl
      });
      
      console.log('🔗 API URL configuration result:', result);
      
      // Verify the URLs were stored correctly by retrieving them
      try {
        const storedUrls = await ApiConfig.getApiBaseUrls();
        console.log('✅ Stored Base URL:', storedUrls.baseApiUrl);
        console.log('✅ Stored Comm URL:', storedUrls.commApiUrl);
        console.log('🔗 CONFIGURING NATIVE API URLS - COMPLETE');
      } catch (err) {
        console.error('❌ Failed to retrieve stored URLs:', err);
      }
      return result;
    } catch (error) {
      console.error('❌ Error configuring native API URLs:', error);
      if (error instanceof Error) {
        console.error('❌ Error details:', error.message);
        console.error('❌ Error stack:', error.stack);
      }
      return null;
    }
  }
  return null;
};

function App() {
  const location = useLocation();
  const persistDispatch = useDispatch();
  const [pendingNotification, setPendingNotification] = useState<any>(null);
  const [initialized, setInitialized] = useState(false);

  // Effect to check for any stored notification data on startup
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Check if there's notification data in localStorage
      const notificationData = localStorage.getItem('notification_data');
      const fromNotification = localStorage.getItem('from_notification');
      
      if (fromNotification === 'true' && notificationData) {
        try {
          console.log('💾 Found stored notification data on app start:', notificationData);
          setPendingNotification(JSON.parse(notificationData));
        } catch (err) {
          console.error('❌ Error parsing stored notification data:', err);
          // Clear invalid data
          localStorage.removeItem('notification_data');
          localStorage.removeItem('from_notification');
        }
      }
    }
  }, []);

  // Handle pending notifications after app is ready
  useEffect(() => {
    if (initialized && pendingNotification && Capacitor.isNativePlatform()) {
      console.log('🔔 Processing pending notification after initialization:', pendingNotification);
      
      // Wait a moment to ensure app is fully loaded
      setTimeout(() => {
        PushNotificationService.handleNotificationAction({
          notification: pendingNotification
        });
        
        // Clear processed notification
        setPendingNotification(null);
      }, 1000);
    }
  }, [initialized, pendingNotification]);

  // Main Capacitor initialization effect
  useEffect(() => {
    // Initialize Capacitor platform-specific features
    const initCapacitor = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          console.log('🚀 INITIALIZING CAPACITOR FEATURES');
          
          // Hide the splash screen with a fade animation
          await SplashScreen.hide({
            fadeOutDuration: 500
          });
          console.log('🌟 SPLASH SCREEN HIDDEN');
          
          // Register app state change listeners
          CapApp.addListener('appStateChange', ({ isActive }) => {
            console.log('App state changed. Is active?', isActive);
            
            // When app becomes active, check for notification data
            if (isActive) {
              const notificationData = localStorage.getItem('notification_data');
              const fromNotification = localStorage.getItem('from_notification');
              
              if (fromNotification === 'true' && notificationData) {
                try {
                  console.log('💾 Found stored notification data on app resume');
                  // Process notification data when app comes to foreground
                  const parsedData = JSON.parse(notificationData);
                  
                  // Add small delay to ensure app is fully ready
                  setTimeout(() => {
                    PushNotificationService.handleNotificationAction({
                      notification: parsedData
                    });
                    
                    // Clear processed data
                    localStorage.removeItem('notification_data');
                    localStorage.removeItem('from_notification');
                  }, 500);
                } catch (err) {
                  console.error('❌ Error handling notification data on resume:', err);
                }
              }
            }
          });
          console.log('📱 APP STATE LISTENERS REGISTERED');
          
          // Listen for deep links, which could be from notifications
          CapApp.addListener('appUrlOpen', (data) => {
            console.log('App opened with URL:', data.url);
            
            // Check if this is a notification deep link
            if (data.url.includes('OPEN_CHAT_ACTIVITY') || 
                data.url.includes('notification') || 
                data.url.includes('chat')) {
              console.log('📲 App opened via notification deep link');
              
              // Extract thread ID if present in URL
              const url = new URL(data.url);
              const threadId = url.searchParams.get('threadId');
              const senderId = url.searchParams.get('senderId');
              
              // Create notification-like object
              const notificationData: any = { 
                click_action: "OPEN_CHAT_ACTIVITY",
                fromNotification: true
              };
              
              if (threadId) notificationData.threadId = threadId;
              if (senderId) notificationData.senderId = senderId;
              
              // Process notification after a short delay
              setTimeout(() => {
                PushNotificationService.handleNotificationAction({
                  notification: notificationData
                });
              }, 500);
            }
          });
          
          // Check for app launch from notification or URL
          const launchUrl = await CapApp.getLaunchUrl();
          if (launchUrl?.url) {
            console.log("📲 App launched with URL:", launchUrl.url);
            
            if (launchUrl.url.includes("OPEN_CHAT_ACTIVITY") || 
                launchUrl.url.includes("notification") || 
                launchUrl.url.includes("chat")) {
              console.log("📲 App launched via notification URL");
              
              // Extract thread ID if present in URL
              const url = new URL(launchUrl.url);
              const threadId = url.searchParams.get('threadId');
              const senderId = url.searchParams.get('senderId');
              
              // Create notification-like object
              const notificationData: any = { 
                click_action: "OPEN_CHAT_ACTIVITY",
                fromNotification: true
              };
              
              if (threadId) notificationData.threadId = threadId;
              if (senderId) notificationData.senderId = senderId;
              
              // Store for processing after initialization
              setPendingNotification(notificationData);
            }
          }
          
          // Network diagnostics initialization
          NetworkDiagnostics.initialize();
          console.log('🌐 NETWORK DIAGNOSTICS INITIALIZED');
          
          // Configure native API URLs
          await configureNativeApiUrls();
          console.log('🔌 NATIVE API URLS CONFIGURED');
          
          // FCM Debugger checks
          console.log('🔔 FCM DEBUG INFO:');
          console.log('🔔 Is Native Platform:', Capacitor.isNativePlatform());
          console.log('🔔 Platform:', Capacitor.getPlatform());
          console.log('🔔 Auth Token Available:', !!localStorage.getItem(LOCALSTORE.token));
          
          // Initialize push notifications
          console.log('🔔 CALLING INITIALIZE PUSH NOTIFICATIONS');
          await PushNotificationService.initializePushNotifications();
          console.log('✅ PUSH NOTIFICATIONS INITIALIZATION COMPLETE');
          
          // Mark app as initialized
          setInitialized(true);
          
        } catch (error) {
          console.error('❌ ERROR INITIALIZING CAPACITOR:', error);
          if (error instanceof Error) {
            console.error('❌ Error name:', error.name);
            console.error('❌ Error message:', error.message);
            console.error('❌ Error stack:', error.stack);
          }
          
          // Even if there's an error, mark as initialized so we can still function
          setInitialized(true);
        }
      } else {
        console.log('🖥️ NON-NATIVE PLATFORM - SKIPPING CAPACITOR INITIALIZATION');
        setInitialized(true);
      }
    };
    
    initCapacitor();
    
    // Try to register FCM token after a short delay (force registration)
    if (Capacitor.isNativePlatform()) {
      setTimeout(() => {
        console.log('🔥 FORCE ATTEMPTING FCM TOKEN REGISTRATION');
        
        // Check if we have a token
        const savedToken = localStorage.getItem('fcm_token');
        if (savedToken) {
          console.log('🔥 EXISTING TOKEN FOUND IN STORAGE:', savedToken.substring(0, 10) + '...');
        } else {
          console.log('🔥 NO EXISTING TOKEN FOUND IN STORAGE');
        }
        
        // Try force registration
        PushNotificationService.registerTokenAtLogin()
          .then(() => {
            console.log('✅ FORCE FCM REGISTRATION ATTEMPT COMPLETED');
            
            // Try saved token registration as fallback after a delay
            setTimeout(() => {
              console.log('🔄 ATTEMPTING FALLBACK SAVED TOKEN REGISTRATION');
              PushNotificationService.registerSavedTokenAfterLogin()
                .then(() => console.log('✅ FALLBACK REGISTRATION COMPLETED'))
                .catch(err => console.error('❌ FALLBACK REGISTRATION FAILED:', err));
            }, 3000);
          })
          .catch(err => {
            console.error('❌ FORCE FCM REGISTRATION FAILED:', err);
            if (err instanceof Error) {
              console.error('❌ Error details:', err.message);
              console.error('❌ Error stack:', err.stack);
            }
          });
      }, 3000); // Reduced from 5000 to speed up token registration
    }
    
    // Return cleanup function
    return () => {
      if (Capacitor.isNativePlatform()) {
        console.log('🧹 CLEANING UP CAPACITOR RESOURCES');
        NetworkDiagnostics.cleanup();
        PushNotificationService.removeListeners();
        
        // Remove app state change listeners
        CapApp.removeAllListeners();
      }
    };
  }, []);

  // Store previous location to detect navigation patterns
  const [prevPathname, setPrevPathname] = useState<string | null>(null);
  
  useEffect(() => {
    if (
      location?.pathname !== "/user/home" &&
      !location?.pathname?.includes("/user/profileDetails")
    ) {
      persistDispatch(reset_home_scroll_position());
    }

    if (
      location?.pathname !== "/consultant/home" &&
      !location?.pathname?.includes("/consultant/viewClientProfile")
    ) {
      persistDispatch(reset_sp_home_scroll_position());
    }
    
    // Detect navigation from chat to home page
    const isChatPath = prevPathname?.includes("/chat") || 
                       prevPathname?.includes("/inbox") || 
                       prevPathname?.includes("/consultchat");
                       
    const isNavigatingToHome = location?.pathname === "/user/home" && 
                               isChatPath && 
                               prevPathname !== location?.pathname;
    
    // Handle navigation from chat to home page
    if (isNavigatingToHome) {
      console.log("Navigation detected from chat to home page");
      
      // Set a flag in sessionStorage to trigger a refresh on the home page
      sessionStorage.setItem('refresh_home_page', 'true');
    }
    
    // Update the previous path for next comparison
    setPrevPathname(location?.pathname);
  }, [location?.pathname, persistDispatch, prevPathname]);

  UsePageTracking();
  
  return (
    <div className={Capacitor.isNativePlatform() ? 'capacitor-app' : ''}>
      <Routes />
    </div>
  );
}

export default App;
